import _ from 'underscore';
import { DeterministicView } from '@bingads-webui/deterministic-view';
import { DatePicker } from '@bingads-webui/date-picker';
import CampaignsDatePickerTemplate from './template.pug';

class ExtendedDatePicker extends DatePicker {
  renderTrunk() {
    super.renderTrunk();

    const { isNodateSelected } = this.getState();

    if (isNodateSelected) {
      this.datePicker.enable(false);
    }
  }
}

/**
 * @typedef {Object} CampaignsDatePickerState
 * @property {Date} selectedDate current selected date.
 * @property {Boolean} allowNoDate allow empty value.
 * If true radio buttons are added to choose if no date is selected.
 * @property {String} noDateLabel label for no date option.
 * @property {String} dateLabel label for the date option.
 */

/**
 * dateChanged event - When date is changed, it fires with the new date object.
 *
 * @event CampaignsDatePicker#dateChanged
 * @type {Date}
 */

/**
 * Date picker control used to select dates.
 * This has optional radio buttons to select no date.
 *
 * @fires CampaignsDatePicker#dateChanged
 */
export class CampaignsDatePicker extends DeterministicView {
  /**
   * Initialize the view.
   * @param {Date} minDate- The minimum date that can be selected.
   * @param {Date} maxDate - The maximum date that can be selected.
   * @param {Date} selectedDate initial selected date.
   * @param {Boolean} allowNoDate allow empty value.
   * @param {String} noDateLabel label for no date option.
   * @param {String} dateLabel label for the date option.
   * @param {String} noDateGroupAriaLabel aria-label for no date group.
   * @param {String} datePickerAriaLabel aria-label for the date option.
   * @returns {undefined}
   */
  initialize({
    minDate,
    maxDate,
    selectedDate,
    allowNoDate,
    noDateLabel,
    dateLabel,
    noDateGroupAriaLabel,
    datePickerAriaLabel,
  }) {
    const isDateValid = !isNaN(new Date(selectedDate)); // eslint-disable-line
    // If we have no date radio option and initial date is not set,
    // we default to choose no date option
    const isNodateSelected = !isDateValid && allowNoDate;
    const datePicker = new ExtendedDatePicker({
      minDate, maxDate, selectedDate, ariaLabel: datePickerAriaLabel,
    });
    const datePickerInput = datePicker.$('.date-picker-component.k-input');
    datePickerInput.addClass('clickable');
    datePickerInput.attr('aria-label', datePickerAriaLabel);

    this.setChild('date-picker', datePicker);
    this.setState({
      selectedDate,
      allowNoDate,
      noDateLabel,
      dateLabel,
      isNodateSelected,
      noDateGroupAriaLabel,
      datePickerAriaLabel,
    });
    this.listenTo(datePicker, 'dateChanged', () => {
      this.trigger('dateChanged', this.getSelectedDate());
    });
  }

  /**
   * The event hash for the CampaignsDatePicker
   * @type {Object}
   * @override
   */
  get events() {
    return {
      'click .nodate': () => this.selectNoDateOption(),
      'click .date': () => this.selectDateOption(),
    };
  }

  /**
   * Class name of the root element
   * @type {string}
   * @override
   */
  get className() { return 'campaigns-date-picker'; }

  /**
   * Template of the CampaignsDatePicker
   * @type {TemplateCallback}
   * @override
   */
  get template() { return CampaignsDatePickerTemplate; }

  /**
   * Propagate the state to model and children view.
   * @param {CampaignsDatePickerState} state - The new state
   * @override
   * @returns {CampaignsDatePicker} - The view itself
   */
  setState(state) {
    const { selectedDate, isNodateSelected } = state;
    this.getChild('date-picker').setState({ selectedDate, isNodateSelected });
    this.model.set(state);
    return this;
  }

  /**
   * Collect the state from model and children views.
   * @override
   * @returns {CampaignsDatePickerState} - The state of the view.
   */
  getState() {
    const state = this.model.attributes;
    const { selectedDate } = this.getChild('date-picker').getState();
    return _.extend(state, { selectedDate });
  }

  /**
   * Get selected date.
   * @returns {Date} - Selected date.
   */
  getSelectedDate() {
    return this.getChild('date-picker').getDate();
  }

  /**
   * No date radio button is selected.
   * Clear the date input. And disable the date picker.
   * @returns {undefined}
   */
  selectNoDateOption() {
    const originalDate = this.getSelectedDate();
    this.setState({ selectedDate: undefined, isNodateSelected: true });
    if (originalDate) {
      this.trigger('dateChanged', this.getSelectedDate());
    }
  }

  /**
   * Date radio button is selected.
   * Enable date picker.
   * @returns {undefined}
   */
  selectDateOption() {
    this.setState({ selectedDate: undefined, isNodateSelected: false });
  }
}
